<template>
  <div class="group">
    <div class="d-flex justify-content-between align-items-center">
      <h3 class="textStyle group__title">
        <TrainingGroupStatusComponent :status="group.status" />
        {{ group.startYear }}-{{ group.endYear }} | {{ group.name }}
      </h3>
      <div class="group__actions">
        <button
          v-if="group.status === currentGroupConstantes.GROUP_STATUS_VALID && dataVersionIsActive"
          v-tooltip="{
            title: $t('registration.group_action.copy_sheet'),
          }"
          class="btn btn-icon btnCopy"
          @click="copyToClipboard"
        >
          <FontAwesomeIcon icon="fa-light fa-copy" />
        </button>
        <button
          v-if="group.status === currentGroupConstantes.GROUP_STATUS_VALID && dataVersionIsActive"
          v-tooltip="{
            title: $t('registration.group_action.create'),
          }"
          class="btn btn-icon btnCopy ms-2"
          @click="createRegistration"
        >
          <FontAwesomeIcon icon="fa-light fa-plus" />
        </button>
        <router-link
          v-if="group.status === currentGroupConstantes.GROUP_STATUS_VALID && dataVersionIsActive"
          v-tooltip="{
            title: $t('registration.group_action.see_sheet'),
          }"
          :to="{
            name: 'TrainingPreRegistration',
            params: { group: group.cryptedId, tab: 0 },
          }"
          class="btn-icon ms-2"
        >
          <FontAwesomeIcon icon="fa-light fa-eye" />
        </router-link>
        <router-link
          v-tooltip="{
            title: this.$tc('global.edit.title', 1, {
              name: this.$tc('studea_manager.group.title', 1).toLowerCase(),
            }),
          }"
          :to="{
            name: 'TrainingGroupEdit',
            params: { id: group.cryptedId, trainingId: training.id },
          }"
          class="btn-icon ms-2"
        >
          <FontAwesomeIcon icon="fa-light fa-pen" />
        </router-link>
        <button
          v-if="group.status !== currentGroupConstantes.GROUP_STATUS_VALID"
          v-access:[permissions]="{
            access: accessConstante.ALL,
            code: accessConstante.STUDEA_MANAGER_TRAINING_GROUP_DELETE,
          }"
          v-tooltip="{
            title: $tc('global.delete.title', 1, {
              name: $tc('studea_manager.group.title', 1).toLowerCase(),
            }),
          }"
          class="btn-icon ms-2"
          @click="deleteGroup(group.cryptedId)"
        >
          <FontAwesomeIcon icon="fa-light fa-trash" />
        </button>
      </div>
    </div>
    <div class="group__registrations">
      <div v-if="group.code" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.code") }}</div>
        <div class="col-7">
          {{ group.code }}
        </div>
      </div>

      <div v-if="group.startDate && group.endDate" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("registration.dates") }}</div>
        <div class="col-7">
          du {{ formatDate(group.startDate) }} au
          {{ formatDate(group.endDate) }}
        </div>
      </div>

      <div v-if="group.hourlyTrainingVolume" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.hours") }}</div>
        <div class="col-7">{{ group.hourlyTrainingVolume }}h</div>
      </div>

      <div v-if="group.hourlyTeachingVolume" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.hours_learning") }}</div>
        <div class="col-7">{{ group.hourlyTeachingVolume }}h</div>
      </div>

      <div v-if="group.contractType" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.public") }}</div>
        <div class="col-7">
          {{ setTargerAudiences() }}
        </div>
      </div>

      <div v-if="group.comment" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.comment") }}</div>
        <div class="col-7">
          <div class="group__comment">{{ group.comment }}</div>
        </div>
      </div>

      <div v-if="group.numberOfRegistrations || group.numberOfRegistrations === 0" class="row mb-1">
        <div class="col-5 fw-bold">{{ $t("training.registrants_number") }}</div>
        <div class="col-7">
          <div class="">
            <router-link
              v-if="group.numberOfRegistrations && group.numberOfRegistrations !== 0"
              class="badge badge-success"
              :to="{
                name: 'RegistrationList',
                query: {
                  group: group.cryptedId,
                },
              }"
              >{{ group.numberOfRegistrations }}</router-link
            >
            <span v-else class="badge badge-success">{{ group.numberOfRegistrations }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { dateFormatter } from "@/services/utilities/date-formatter";
import TrainingGroupStatusComponent from "@/components/training/TrainingGroupStatusComponent.vue";
import { registrationConstante } from "@/constantes/registration";
import { groupConstante } from "@/constantes/group";
import { registrationManager } from "@/services/registration/registration-manager";
import { mapState } from "pinia";
import { usePlatformStore } from "@/store/platform/platform";
import { useUserStore } from "@/store/user/user";
import { accessConstante } from "@/constantes/access";

export default {
  name: "TrainingGroupComponent",
  components: { TrainingGroupStatusComponent, FontAwesomeIcon },
  props: {
    group: {
      type: Object,
      required: true,
    },
    training: {
      type: Object,
      required: true,
    },
  },
  emits: ["createRegistration", "deleteGroup"],
  data() {
    return {};
  },
  computed: {
    currentGroupConstantes() {
      return groupConstante;
    },
    accessConstante() {
      return accessConstante;
    },
    tooltip() {
      switch (this.group.status) {
        case groupConstante.GROUP_STATUS_PENDING:
          return this.$t("registration.workInProgress");
        case groupConstante.GROUP_STATUS_VALID:
          return this.$t("registration.valid");
        default:
          return this.$t("registration.pending");
      }
    },

    ...mapState(usePlatformStore, {
      dataVersionIsActive: (store) => store.dataVersionIsActive,
    }),
    ...mapState(useUserStore, {
      permissions: (store) => store.permissions,
    }),
  },
  methods: {
    formatDate(date) {
      return dateFormatter.format(date, "short");
    },

    icon() {
      switch (this.group.status) {
        case groupConstante.GROUP_STATUS_PENDING:
          return "info";
        case groupConstante.GROUP_STATUS_VALID:
          return "check";
        default:
          return "timer";
      }
    },

    copyToClipboard() {
      registrationManager.copyToClipboard(this.group.cryptedId);
    },

    createRegistration() {
      this.$emit("createRegistration", this.group.cryptedId);
    },

    setTargerAudiences() {
      let targetAudiences = "";

      this.group.contractType.forEach((contractType) => {
        if (targetAudiences.length) {
          targetAudiences += " / ";
        }

        targetAudiences += this.$t(registrationConstante.contractTypeTargetAudiencesLabels[contractType]);
      });

      return targetAudiences;
    },

    deleteGroup(cryptedId) {
      this.$emit("deleteGroup", cryptedId);
    },
  },
};
</script>

<style lang="scss" scoped>
.group {
  border: 1px solid $m_color_4;
  padding: 15px;
  border-radius: $radius;
  height: 100%;

  &__title {
    font-size: 16px;
    margin-bottom: 0;
  }

  &__registrations {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed $m_color_4;
  }

  &__registration {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__registration + &__registration {
    margin-top: 5px;
  }

  &__comment {
    white-space: pre;
  }

  &__status {
    background: var(--registration-waiting);
    color: $white;
    width: 23px;
    height: 23px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: 13px;
    border-radius: 50%;
    margin-right: 5px;

    &.success {
      background: var(--registration-success);
    }

    &.warning {
      background: var(--registration-progress);
    }
  }

  .btnCopy svg {
    width: 17px;
  }

  &__actions {
    display: flex;
    justify-content: center;
  }

  .badge {
    &.badge-success {
      border: 1px solid $green;
      &:hover {
        background-color: $white;
        color: $green;
      }
    }
  }
}
</style>
